import {
  getObject,
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class UnassignRespondentFromSpocRPC implements
  LeoRPC<UnassignRespondentFromSpocRPC.Request, UnassignRespondentFromSpocRPC.Response, UnassignRespondentFromSpocRPC.Errors.Errors>
{
  abstract execute(
    request: UnassignRespondentFromSpocRPC.Request
  ): Promise<LeoRPCResult<UnassignRespondentFromSpocRPC.Response, UnassignRespondentFromSpocRPC.Errors.Errors>>;
}

export namespace UnassignRespondentFromSpocRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    respondentSelection: RespondentSelection.RespondentSelectionDTO;
    spocId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUnassignRespondentFromSpocRPCRequest: boolean = true;
  
    readonly respondentSelection: RespondentSelection.RespondentSelection;
    readonly spocId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    respondentSelection: RespondentSelection.RespondentSelection, 
    spocId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.respondentSelection = respondentSelection;
      this.spocId = spocId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const respondentSelection = RespondentSelection.fromDTO(getObject(dto, "respondentSelection", InvalidRequestRespondentSelectionError));
      const spocId = getLeoUuid(dto, "spocId", InvalidRequestSpocIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        respondentSelection,
        spocId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          respondentSelection: this.respondentSelection.toDTO(),
          spocId: this.spocId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UnassignRespondentFromSpocRPC(respondentSelection='${this.respondentSelection}',spocId='${this.spocId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UnassignRespondentFromSpocRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSpocId = "INVALID_SPOC_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER",
    InvalidRespondentIdFound = "INVALID_RESPONDENT_ID_FOUND"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UnassignRespondentFromSpocRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `UnassignRespondentFromSpocRPC.INVALID_SPOC_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UnassignRespondentFromSpocRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `UnassignRespondentFromSpocRPC.DELETED_COLUMN_FILTER()`;
    };
    
    
    export interface InvalidRespondentIdFoundDTO {
      code: string;
    }
    
    export class InvalidRespondentIdFound extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentIdFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentIdFound;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdFoundDTO {
        const dto: InvalidRespondentIdFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentIdFound.prototype.toString = function toString(): string {
      return `UnassignRespondentFromSpocRPC.INVALID_RESPONDENT_ID_FOUND()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSpocId | ProjectAlreadyArchived | DeletedColumnFilter | InvalidRespondentIdFound;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSpocIdDTO | ProjectAlreadyArchivedDTO | DeletedColumnFilterDTO | InvalidRespondentIdFoundDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        case RPCError.InvalidRespondentIdFound:
          return new InvalidRespondentIdFound();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestRespondentSelectionError extends UnassignRespondentFromSpocRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentSelectionError";
  }
}

export class InvalidRequestSpocIdError extends UnassignRespondentFromSpocRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdError";
  }
}

export class InvalidRequestProjectIdError extends UnassignRespondentFromSpocRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}


export class InvalidResponseInvalidProjectIdError extends UnassignRespondentFromSpocRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSpocIdError extends UnassignRespondentFromSpocRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UnassignRespondentFromSpocRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends UnassignRespondentFromSpocRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

export class InvalidResponseInvalidRespondentIdFoundError extends UnassignRespondentFromSpocRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdFoundError";
  }
}

